import { isAndroidDevice, isIosDevice } from "./arCapability";
import type { ArParkedUrls } from "./arDelivery";

// The AR-launch PLATFORM ROUTING core (ADR-0035 §7, issue #383) — the pure decision
// layer behind the desktop → QR → phone handoff. It answers two questions with NO
// DOM and NO globals, so the whole iOS / Android / other × has-asset / missing-asset
// matrix unit-tests as plain data:
//
//   1. `arLaunchPlatform(ua, touch)` — which OS family opened the AR-launch page,
//      reusing the SAME device detection the capability probe uses (never a second,
//      drift-prone copy of the UA regexes).
//   2. `resolveArLaunch(platform, urls)` — given that platform and the parked asset
//      URLs (carried on the launch URL's query), WHICH OS AR engine to open and with
//      WHICH asset, or WHY it can't: iOS → Quick Look (USDZ), Android → Scene Viewer
//      (GLB), a non-AR platform → `unsupported`, an absent/expired asset →
//      `missing-asset`.
//
// It also owns `buildArLaunchUrl` — the encoder the DESKTOP side (`ArQrHandoffButton`)
// uses to fold the parked `https` URLs into the launch URL the QR encodes. Encoder
// (desktop) and resolver (phone) live together so the query contract — the `glbUrl` /
// `usdzUrl` param names, keyed off `ArParkedUrls` — can never drift between the two
// ends of the handoff.
//
// This is the whole point of the QR handoff (ADR-0035 §7): the phone opens a HOSTED
// ASSET off a self-authenticating signed URL, never the session-gated preview — so
// `GET /jobs/{id}`'s 403 is never in the path. This module carries no session, no
// token, nothing but the URLs already signed by the park endpoint (#379).

/** The OS family that opened the AR-launch page. `other` = no on-device OS AR. */
export type ArLaunchPlatform = "ios" | "android" | "other";

/**
 * Classify the opening device into the OS family whose AR engine we can launch —
 * reusing the capability probe's `isIosDevice` / `isAndroidDevice` (which already
 * handle the iPadOS "desktop Macintosh + touch" masquerade), so there is ONE UA
 * detection in the codebase, not two. A desktop/other UA → `other` (the page then
 * tells the visitor to open the link on a phone).
 */
export function arLaunchPlatform(userAgent: string, maxTouchPoints: number): ArLaunchPlatform {
  if (isIosDevice(userAgent, maxTouchPoints)) return "ios";
  if (isAndroidDevice(userAgent)) return "android";
  return "other";
}

/**
 * The launch decision. A discriminated union so the AR-launch page renders exactly
 * one of: the OS-AR launch (`quick-look` / `scene-viewer`, each carrying the asset
 * its engine consumes), the "open this on a phone" notice (`unsupported`), or the
 * "the link expired — re-generate on desktop" notice (`missing-asset`).
 */
export type ArLaunchResolution =
  | { kind: "quick-look"; usdzUrl: string }
  | { kind: "scene-viewer"; glbUrl: string }
  | { kind: "unsupported" }
  | { kind: "missing-asset" };

/**
 * A launch URL param is only usable if it is a real, network-fetchable `http(s)`
 * address (the same invariant `arDelivery` enforces on the park response, ADR-0035
 * §3): the OS AR engines fetch it out-of-process. Since these arrive from the QUERY
 * STRING — attacker-controllable in a crafted link — anything else (`blob:`,
 * `javascript:`, `data:`, empty, unparseable) is treated as ABSENT here, so it can
 * never reach a `<model-viewer src>` or an `<a href>` downstream. `http` is allowed
 * so a localstack-signed dev URL still resolves; production URLs are `https`.
 *
 * Exported so the AR-launch page can scheme-guard the URLs it hands the launcher
 * (`<model-viewer src>` / `ios-src`) with the EXACT same predicate `resolveArLaunch`
 * uses for gating — the invariant must hold on the pass-through field too, not just
 * the gating one, so a crafted param can never reach the element regardless of which
 * platform / `ar-mode` ends up reading it.
 */
export function fetchableHttpUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  let protocol: string;
  try {
    protocol = new URL(url).protocol;
  } catch {
    return undefined;
  }
  return protocol === "https:" || protocol === "http:" ? url : undefined;
}

/**
 * Route the phone to the right OS AR engine (ADR-0035 §2/§3, §7). iOS has no WebXR,
 * so its ONLY path is AR Quick Look off the parked USDZ; Android opens Scene Viewer
 * (or WebXR) off the parked GLB — the exact split the capability probe classifies as
 * `native` on those devices. A platform with no on-device OS AR (`other`, e.g. a
 * desktop that opened the link) → `unsupported`; a platform whose asset is absent or
 * not fetchable (an expired short-TTL park, or a truncated link) → `missing-asset`.
 * Pure: the asset presence + validity is all decided from the passed URLs.
 */
export function resolveArLaunch(
  platform: ArLaunchPlatform,
  urls: ArParkedUrls,
): ArLaunchResolution {
  const glbUrl = fetchableHttpUrl(urls.glbUrl);
  const usdzUrl = fetchableHttpUrl(urls.usdzUrl);
  if (platform === "ios") {
    return usdzUrl ? { kind: "quick-look", usdzUrl } : { kind: "missing-asset" };
  }
  if (platform === "android") {
    return glbUrl ? { kind: "scene-viewer", glbUrl } : { kind: "missing-asset" };
  }
  return { kind: "unsupported" };
}

/** The AR-launch route path — the single source both the encoder and the route use. */
export const AR_LAUNCH_PATH = "/ar-launch";

/**
 * Encode the parked asset URLs into the ABSOLUTE AR-launch URL the desktop QR
 * encodes and offers as a copyable link (ADR-0035 §7). Stateless: the signed
 * `https` URLs are self-authenticating, so the whole handoff is carried in the
 * query — no token, no session, no server round-trip. `URL` handles the percent-
 * encoding of the signed URLs (query strings, signatures) correctly; a format that
 * was not parked is simply omitted, and `resolveArLaunch` treats an omitted asset as
 * `missing-asset` on the far end. `origin` is injected (never read from a global) so
 * this stays pure and testable.
 */
export function buildArLaunchUrl(origin: string, urls: ArParkedUrls): string {
  const url = new URL(AR_LAUNCH_PATH, origin);
  if (urls.glbUrl) url.searchParams.set("glbUrl", urls.glbUrl);
  if (urls.usdzUrl) url.searchParams.set("usdzUrl", urls.usdzUrl);
  return url.toString();
}
